import {
  Box,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import CircularLoading from '../../../../../components/CircularProgress'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import {
  ImageLinks,
  LogoTypeProvider,
} from '../../../../../constants/image-link'
import PageUrls from '../../../../../constants/page-urls'
import { axiosInstance } from '../../../../../hooks/useAxios'
import { EOrderStatus, IOrder } from '../../../../../types/order'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IFilterMapped } from './DailySalesReportFilter'
import { numberFormat } from '../../../../../utils'

interface IDailySalesReportOrderTableModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  date?: { startDate: string; endDate: string }
  filter: IFilterMapped
}

interface OrganizedData {
  label: string
  data: {
    orderCode: number
    distribution: string
    quantity: number
    price: number
    // items: any[];
  }[]
}

interface IDistribution {
  name: string
  type: string
}

const menuItemDisplayList = [
  { value: 'category', label: 'แสดงหมวดหมู่' },
  { value: 'product', label: 'แสดงรายชื่อสินค้า' },
]

export const DailySalesReportOrderTableModal = (
  props: IDailySalesReportOrderTableModalProps,
) => {
  const { isOpen, onClose, date, filter } = props
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [list, setList] = useState<OrganizedData[]>()
  const [distributionList, setDistributionList] = useState<IDistribution[]>([])
  const [selectDistributionChannel, setSelectDistributionChannel] =
    useState<string>('')
  const [selectDisplayType, setSelectDispatchType] = useState<string>('')
  useEffect(() => {
    try {
      const fetchOrders = async () => {
        setIsLoading(true)
        const exceptStatus = [
          EOrderStatus.CANCLELLED,
          ...(filter.includeReturned === 'true' ? [] : [EOrderStatus.RETURNED]),
          EOrderStatus.TELESALE,
          EOrderStatus.AI,
        ]
        const response = await axiosInstance.get<
          PaginationResponseInterface<IOrder>
        >(`/order`, {
          params: {
            page: 1,
            perPage: 999999,
            exceptStatus,
            // statuses,
            startDate: date?.startDate,
            endDate: date?.endDate,
            statusShowCrmTelesale: filter?.status,
            dateType: 'orderDate',
            statusOrderShowTracking: filter?.trackingStatus,
            products: filter?.productObjectIds ?? [],
            productGroups: filter?.productGroups,
            categories: filter?.categoryObjectIds ?? [],
            distributionChannels: filter.distributionObjectIds ?? [],
            isAi: filter.isAi !== 'all' ? filter.isAi : undefined,
          },
        })

        const unique = Array.from(
          new Set(
            response.data.records?.flatMap((order) =>
              order.items.map((item) =>
                selectDisplayType === 'product'
                  ? item.productName
                  : item.categoryName,
              ),
            ),
          ),
        )

        const data = unique.map((label) => {
          const ordersInLabel = response.data.records?.filter((order) =>
            order.items.some((item) =>
              selectDisplayType === 'product'
                ? item.productName === label
                : item.categoryName === label,
            ),
          )

          //console.log('ordersInLabel', ordersInLabel)
          return {
            label,
            data: ordersInLabel.map((order) => ({
              orderCode: order.code,
              distribution: order.distribution?.name,
              quantity: order.items.reduce(
                (pre, cur) =>
                  pre +
                  (selectDisplayType === 'product'
                    ? cur.productName === label
                      ? cur.quantity
                      : 0
                    : cur.categoryName === label
                      ? cur.quantity
                      : 0),
                0,
              ),
              price: order.items.reduce(
                (pre, cur) =>
                  pre +
                  (selectDisplayType === 'product'
                    ? cur.productName === label
                      ? cur.total
                      : 0
                    : cur.categoryName === label
                      ? cur.total
                      : 0),
                0,
              ),
            })),
          }
        })

        //sort data by price
        data.sort((a, b) => {
          const totalA = a.data.reduce((sum, item) => sum + item.price, 0)
          const totalB = b.data.reduce((sum, item) => sum + item.price, 0)
          return totalB - totalA // Sort in descending order
        })

        // console.log('data', data)
        setDistributionList(
          response.data.records
            ?.filter(
              (order, index, self) =>
                index ===
                self.findIndex(
                  (o) =>
                    o.distribution?.name === order.distribution?.name &&
                    o.distribution?.type === order.distribution?.type &&
                    o.distribution?.name,
                ),
            )
            .map((order) => ({
              name: order.distribution?.name || '',
              type: order.distribution?.type || '',
            })),
        )
        setList(data as OrganizedData[])
      }
      fetchOrders()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    } finally {
      setIsLoading(false)
    }
  }, [isOpen, date, selectDisplayType])

  const columns: TableColumnsType<OrganizedData & { key: number }> = [
    {
      title: 'รายการสินค้า',
      dataIndex: 'label',
      key: 'label',
      width: '50%',
      align: 'left' as const,
      render: (label: string, data: OrganizedData) => {
        return (
          <Box>
            <Typography variant="subtitle1" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {/* {data?.data?.map((item, index) => (
                <Box key={index}>
                  <Typography variant="caption" color="text.secondary">
                    {item.orderCode}
                  </Typography>
                </Box>
              ))} */}
              <CollapsibleList
                organizedData={{
                  label: 'รายการคำสั่งซื้อ',
                  data: data.data,
                }}
              />
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'จำนวน ORDER',
      dataIndex: 'data',
      key: 'data',
      align: 'center' as const,
      render: (data) => {
        return (
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {numberFormat(data.length || 0)}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'data',
      key: 'data',
      width: 140,
      align: 'center' as const,
      render: (_, data) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {numberFormat(
                data?.data?.reduce((pre, cur) => pre + cur.quantity, 0),
              )}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'ยอดขาย',
      dataIndex: 'data',
      key: 'data',
      width: 140,
      align: 'center' as const,
      render: (_, data) => {
        return (
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {numberFormat(
                data?.data?.reduce((pre, cur) => pre + cur.price, 0),
              )}
            </Typography>
          </Box>
        )
      },
    },
  ]

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '1024px!important',
        },
      }}
    >
      <CircularLoading isLoading={isLoading} />
      <DialogTitle>
        รายละเอียดวันที่ {dayjs(date?.endDate).format(ONLY_DATE_FORMAT)}
      </DialogTitle>
      <DialogContent>
        <CircularLoading isLoading={isLoading} />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          <Grid item xs={12} md={6} lg={6}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="selectDisplayType-label">
                รูปแบบการแสดงผล
              </InputLabel>
              <Select
                id="selectDisplayType"
                name="selectDisplayType"
                labelId="selectDisplayType-label"
                value={selectDisplayType}
                onChange={(e) =>
                  setSelectDispatchType(e.target.value as string)
                }
                fullWidth
              >
                <MenuItem disabled value="" selected>
                  <em>เลือกวันหมดอายุ</em>
                </MenuItem>
                {menuItemDisplayList.map((item, index: number) => (
                  <MenuItem value={item.value} key={index}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="selectDistributionChannel-label">
                ช่องทางการขาย
              </InputLabel>
              <Select
                id="selectDistributionChannel"
                name="selectDistributionChannel"
                labelId="selectDistributionChannel-label"
                value={selectDistributionChannel}
                onChange={(e) =>
                  setSelectDistributionChannel(e.target.value as string)
                }
                fullWidth
              >
                <MenuItem disabled value="" selected>
                  <em>เลือกช่องทางการขาย</em>
                </MenuItem>
                <MenuItem value="all">
                  <em>ทั้งหมด</em>
                </MenuItem>
                {distributionList?.map((item, index: number) => (
                  <MenuItem value={item?.name} key={index}>
                    {item.type && (
                      <img
                        style={{ marginRight: 5 }}
                        src={
                          ImageLinks[
                            `LOGO_${item.type.toUpperCase()}` as LogoTypeProvider
                          ]
                        }
                        alt="smallLogoSocial"
                        height={20}
                      />
                    )}
                    {item?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <Table
              size={'middle'}
              columns={columns}
              scroll={{
                x: columns.reduce(
                  (pre, cur: any) => (cur.width ? cur.width + pre : pre),
                  0,
                ),
              }}
              dataSource={list?.map((data: OrganizedData, index: number) => {
                return {
                  ...data,
                  key: index,
                }
              })}
              bordered
              pagination={false}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

const CollapsibleList: React.FC<{ organizedData: OrganizedData }> = ({
  organizedData,
}) => {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const handleToggle = () => {
    setOpen(!open)
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" color="text.secondary">
          {organizedData.label}
        </Typography>
        <IconButton onClick={handleToggle}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List>
          {organizedData.data.map((item, index) => (
            <ListItem key={index}>
              <ListItemText
                primary={
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    onClick={
                      item.orderCode
                        ? () => {
                            navigate(
                              `${PageUrls.Orders.Detail}/${item.orderCode}`,
                              {
                                replace: true,
                              },
                            )
                          }
                        : undefined
                    }
                    sx={{ cursor: item.orderCode ? 'pointer' : 'default' }}
                  >
                    {item.orderCode}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  )
}
